import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtKpi } from "./kpiFormat";

export const toSplit = (rows) => (rows || []).map((r) => ({ key: r.key || "", name: r.name || "", amount: r.amount ?? "", typed: false }));
export const splitPayload = (split) => split.map((r) => ({ key: r.key, name: r.name.trim(), amount: Number(r.amount || 0) }));
export const splitTotal = (split) => split.reduce((n, r) => n + (Number(r.amount) || 0), 0);

const SplitLine = ({ r, i, leads, onChange, onRemove, week }) => (
  <div className="flex items-center gap-2" data-testid={`kpi-split-row-${week}-${i}`}>
    {r.typed ? (
      <Input value={r.name} onChange={(e) => onChange({ ...r, name: e.target.value, key: "" })} placeholder="Campaign name"
        className="h-8 w-64" data-testid={`kpi-split-name-${week}-${i}`} autoFocus />
    ) : <span className="w-64 truncate text-sm text-zinc-800" title={r.name}>{r.name}</span>}
    <div className="relative w-28">
      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-400">$</span>
      <Input type="number" min={0} step="0.01" value={r.amount} onChange={(e) => onChange({ ...r, amount: e.target.value })}
        className="h-8 pl-6 font-mono-num" data-testid={`kpi-split-amount-${week}-${i}`} />
    </div>
    <span className="w-20 text-xs text-zinc-500">{leads != null ? `${leads} lead(s)` : ""}</span>
    <button type="button" onClick={onRemove} className="text-zinc-400 transition-colors hover:text-red-600" title="Remove"
      data-testid={`kpi-split-remove-${week}-${i}`}><X className="h-4 w-4" /></button>
  </div>
);

/** Optional split of a week's Facebook spend across campaigns; the weekly total stays as entered. */
export const CampaignSplit = ({ week, total, split, setSplit, suggested }) => {
  const leadsFor = (r) => suggested.find((s) => (r.key && s.key === r.key) || s.name.toLowerCase() === r.name.trim().toLowerCase())?.leads;
  const unused = suggested.filter((s) => !split.some((r) => r.key === s.key || r.name.trim().toLowerCase() === s.name.toLowerCase()));
  const sum = splitTotal(split);
  const rest = (Number(total) || 0) - sum;
  const set = (i, r) => setSplit(split.map((x, j) => (j === i ? r : x)));
  return (
    <div className="space-y-2 rounded-md border border-zinc-200 bg-zinc-50/70 p-3" data-testid={`kpi-split-${week}`}>
      {split.map((r, i) => (
        <SplitLine key={i} r={r} i={i} week={week} leads={leadsFor(r)} onChange={(v) => set(i, v)}
          onRemove={() => setSplit(split.filter((_, j) => j !== i))} />
      ))}
      <div className="flex flex-wrap items-center gap-1.5">
        {unused.map((s) => (
          <button key={s.key} type="button" onClick={() => setSplit([...split, { key: s.key, name: s.name, amount: "", typed: false }])}
            data-testid={`kpi-split-suggest-${week}-${s.key}`}
            className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-white px-2.5 py-0.5 text-xs text-blue-800 transition-colors hover:bg-blue-50">
            <Plus className="h-3 w-3" />{s.name}<span className="text-blue-500">· {s.leads} lead(s)</span>
          </button>
        ))}
        <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" data-testid={`kpi-split-add-${week}`}
          onClick={() => setSplit([...split, { key: "", name: "", amount: "", typed: true }])}>
          <Plus className="mr-1 h-3 w-3" />Add campaign
        </Button>
      </div>
      {split.length > 0 && (
        <div className={`text-xs ${rest < -0.004 ? "text-red-600" : "text-zinc-500"}`} data-testid={`kpi-split-summary-${week}`}>
          Split {fmtKpi(sum, "money2")} of {total === "" ? "—" : fmtKpi(Number(total), "money2")}
          {rest < -0.004 ? ` — ${fmtKpi(-rest, "money2")} more than the week's total` : rest > 0.004 ? ` · ${fmtKpi(rest, "money2")} not split` : ""}
        </div>
      )}
      {!split.length && !unused.length && <p className="text-xs text-zinc-500">No paid leads with a campaign this week — add a campaign by name.</p>}
    </div>
  );
};
